import { type JSX, useCallback } from 'react';

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { z } from 'zod';

import { PageContent } from '@/components';
import { DashboardGrid } from '@/features/performance-dashboard/components/DashboardGrid/DashboardGrid';
import type { PerformanceDashboardUrlFilters } from '@/features/performance-dashboard/lib/performanceDashboardFilters.schema';
import { useStudentContext } from '@/features/performance-dashboard/pages/Student/StudentContext';
import { StudentHeader } from '@/features/performance-dashboard/pages/Student/StudentHeader';
import { StudentProvider } from '@/features/performance-dashboard/pages/Student/StudentProvider';
import { requireAccess } from '@/permissions';
import '../../styles.scss';

const studentRouteSearchSchema = z.object({
  anoLetivo: z.number().int().nullable().optional(),
  categoria: z.number().int().optional(),
  modelo: z.string().optional(),
});

type StudentRouteSearch = z.infer<typeof studentRouteSearchSchema>;

export const Route = createFileRoute('/resultados/aluno/')({
  staticData: { title: 'Meus resultados' },
  beforeLoad: requireAccess('view:student_dashboard'),
  validateSearch: (search: Record<string, unknown>): StudentRouteSearch => studentRouteSearchSchema.parse(search),
  component: StudentRoute,
});

function StudentGrid(): JSX.Element {
  const { filters, dashboardResults, isLoading } = useStudentContext();
  return (
    <DashboardGrid
      isStudentScoped
      filters={filters}
      dashboardResults={dashboardResults}
      isLoading={isLoading}
    />
  );
}

function StudentRoute(): JSX.Element {
  const { anoLetivo, categoria, modelo } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  const handleNavigate = useCallback(
    ({ search: nextSearch, replace }: { search: PerformanceDashboardUrlFilters; replace?: boolean }) => {
      void navigate({
        search: (prev: StudentRouteSearch) => ({
          ...prev,
          anoLetivo: nextSearch.ano ?? prev.anoLetivo,
          categoria: nextSearch.categoria,
          modelo: nextSearch.modelo,
        }),
        replace,
      });
    },
    [navigate]
  );

  return (
    <StudentProvider
      search={{ ano: anoLetivo, categoria, modelo }}
      navigate={handleNavigate}>
      <StudentHeader />
      <PageContent>
        <StudentGrid />
      </PageContent>
    </StudentProvider>
  );
}
